import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Server price of a scooter rental for the selected dates (GET
 * /services/{id}/pricing-preview — the same engine as the booking). The
 * browser never computes the price.
 *
 * Returns { status: 'idle' | 'loading' | 'ok' | 'error', summary, retry }.
 * `summary` is only returned for the dates currently selected: while a new
 * request runs, or after an error, there is no price (never an older total
 * shown as if it matched the new dates). Late answers are ignored.
 */
export function useScooterQuote(serviceId, startDate, endDate, startTime, endTime) {
  const [state, setState] = useState({ status: 'idle', key: null, summary: null });
  const [attempt, setAttempt] = useState(0);
  const seq = useRef(0);
  const complete = !!(serviceId && startDate && endDate && startTime && endTime);
  const key = complete ? [serviceId, startDate, endDate, startTime, endTime].join('|') : null;

  useEffect(() => {
    const id = ++seq.current;
    if (!key) { setState({ status: 'idle', key: null, summary: null }); return undefined; }
    const controller = new AbortController();
    setState({ status: 'loading', key, summary: null });
    axios.get(`${API}/services/${serviceId}/pricing-preview`, {
      params: { start_date: startDate, end_date: endDate, start_time: startTime, end_time: endTime },
      signal: controller.signal,
    }).then(({ data }) => {
      if (id !== seq.current) return;
      if (data && data.summary) setState({ status: 'ok', key, summary: data.summary });
      else setState({ status: 'error', key, summary: null });
    }).catch((err) => {
      if (id !== seq.current || axios.isCancel?.(err) || err?.name === 'CanceledError') return;
      setState({ status: 'error', key, summary: null, detail: err.response?.data?.detail });
    });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  const current = state.key === key ? state : { status: key ? 'loading' : 'idle', summary: null };
  return { ...current, retry };
}
